import type { Hint, RobotRepairPayload, SectorType } from "@monekineko/game-engine";
import { MOVES } from "@monekineko/game-engine";
import {
  $,
  $$,
  Cutscene,
  actorMarkup,
  rand,
  sfx,
  shuffle,
  sleep,
  spawnSpark,
  vibrate,
  virusHide,
  virusParts,
  virusReset,
  virusRun,
  virusScare,
  virusTinker,
} from "./core.js";
import type { EngineContext, GameEngine } from "./types.js";

// Уровень «Починка роботом». Перенос prototypes/1/js/level2.js. Поле приходит
// с сервера, прогон программы показывается здесь, а засчитывает его сервер:
// он крутит ту же программу по тому же полю тем же исполнителем.

type Command = "up" | "down" | "left" | "right" | "fix";

const ICONS = {
  arrow: '<path d="M12 3.5l7.5 8.5h-4.8v8.5H9.3V12H4.5z"/>',
  fix: '<path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>',
  undo: '<path d="M22 3H7c-.7 0-1.2.4-1.6.9L0 12l5.4 8.1c.4.5.9.9 1.6.9h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.6L17.6 17 14 13.4 10.4 17 9 15.6 12.6 12 9 8.4 10.4 7 14 10.6 17.6 7 19 8.4 15.4 12z"/>',
  play: '<path d="M8 5v14l11-7z"/>',
};

const svg = (path: string, cls = ""): string =>
  `<svg class="ico ${cls}" viewBox="0 0 24 24" aria-hidden="true">${path}</svg>`;

const cmdIcon = (cmd: string): string =>
  cmd === "fix" ? svg(ICONS.fix) : svg(ICONS.arrow, `dir-${cmd}`);

const KEY_LABEL: Record<Command, string> = {
  up: "Вверх",
  down: "Вниз",
  left: "Влево",
  right: "Вправо",
  fix: "Ремонт",
};

const markup = (commands: Command[]): string => `
  <div class="screen active" data-level="robot_repair">
    <div class="l2">
      <div class="hdd-bg"></div>
      <div class="l2-layout">
        <div class="disk-area">
          <div class="disk">
            <div class="scanline"></div>
            <div class="robot"><img src="/assets/robot.webp" alt="Робот"></div>
          </div>
        </div>
        <div class="console-wrap">
          <div class="console">
            <div class="lcd"><div class="lcd-text"></div></div>
            <div class="prog-stack-wrap">
              <div class="prog-stack"></div>
              <div class="prog-stack-glass"></div>
            </div>
            <div class="controls">
              <div class="dpad">
                ${commands.includes("up") ? '<button class="key key-up" data-cmd="up" aria-label="Вверх"></button>' : ""}
                ${commands.includes("left") ? '<button class="key key-left" data-cmd="left" aria-label="Влево"></button>' : ""}
                <span class="dpad-hub"></span>
                ${commands.includes("right") ? '<button class="key key-right" data-cmd="right" aria-label="Вправо"></button>' : ""}
                ${commands.includes("down") ? '<button class="key key-down" data-cmd="down" aria-label="Вниз"></button>' : ""}
              </div>
              <div class="side-keys">
                ${commands.includes("fix") ? '<button class="key key-fix" data-cmd="fix" aria-label="Ремонт"></button>' : ""}
                <button class="key key-undo" data-action="undo" aria-label="Стереть последнюю команду"></button>
              </div>
              <div class="run-wrap">
                <button class="run" data-action="run" aria-label="Пуск"></button>
                <span class="run-label">Пуск</span>
              </div>
            </div>
          </div>
          ${actorMarkup("backHidden")}
          <div class="fx"></div>
        </div>
      </div>
    </div>
    <div class="cabinet">
      <div class="cab-stage">
        <div class="cab-leaf cab-left">
          <div class="cab-face cab-front"></div>
          <div class="cab-face cab-back"></div>
        </div>
        <div class="cab-leaf cab-right">
          <div class="cab-face cab-front"></div>
          <div class="cab-face cab-back"></div>
        </div>
      </div>
    </div>
    <div class="city" hidden>
      <div class="city-stage"></div>
      <div class="rain"></div>
      <div class="city-caption">Кибергород</div>
    </div>
    <div class="whiteout"></div>
  </div>`;

export function createRobotRepair(root: HTMLElement, ctx: EngineContext): GameEngine {
  const payload = ctx.payload as RobotRepairPayload;
  const { cols, rows, stepMs, stackCapacity } = payload;
  const commands = payload.commands as Command[];

  root.innerHTML = markup(commands);

  const screen = $(".screen", root)!;
  const l2 = $(".l2", root)!;
  const diskArea = $(".disk-area", root)!;
  const disk = $(".disk", root)!;
  const scanline = $(".scanline", root)!;
  const robot = $(".robot", root)!;
  const robotImg = $<HTMLImageElement>(".robot img", root)!;
  const consoleEl = $(".console", root)!;
  const lcd = $(".lcd", root)!;
  const lcdText = $(".lcd-text", root)!;
  const stackEl = $(".prog-stack", root)!;
  const consoleFx = $(".console-wrap .fx", root)!;
  const cabinet = $(".cabinet", root)!;
  const cabStage = $(".cab-stage", root)!;
  const city = $(".city", root)!;
  const cityStage = $(".city-stage", root)!;
  const caption = $(".city-caption", root)!;
  const whiteout = $(".whiteout", root)!;
  const virus = virusParts($(".console-wrap .actor", root)!);

  const state = {
    field: payload.field.map((row) => [...row]) as SectorType[][],
    robot: { ...payload.start },
    program: [] as Command[],
    phase: "idle" as "idle" | "intro" | "edit" | "run" | "done",
  };
  let cut: Cutscene | null = null;
  let alive = true;

  // ----- разметка поля и стека -----

  disk.style.setProperty("--cols", String(cols));
  const sectors: HTMLElement[][] = [];
  for (let y = 0; y < rows; y++) {
    sectors.push([]);
    for (let x = 0; x < cols; x++) {
      const cell = document.createElement("div");
      cell.className = "sector";
      disk.insertBefore(cell, scanline);
      sectors[y]!.push(cell);
    }
  }

  const slots: HTMLElement[] = [];
  for (let i = 0; i < stackCapacity; i++) {
    const slot = document.createElement("div");
    slot.className = "slot";
    stackEl.appendChild(slot);
    slots.push(slot);
  }

  $$<HTMLElement>(".key[data-cmd]", root).forEach((key) => {
    key.innerHTML = cmdIcon(key.dataset.cmd!);
  });
  $(".key-undo", root)!.innerHTML = svg(ICONS.undo);
  $(".run", root)!.innerHTML = svg(ICONS.play);

  // Размер клетки подбирается под свободное место над пультом
  function fitDisk(): void {
    const rect = diskArea.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const pad = 16;
    const cell = Math.floor(Math.min((rect.width - pad) / cols, (rect.height - pad) / rows));
    l2.style.setProperty("--cell", `${Math.max(18, cell)}px`);
  }
  const observer = new ResizeObserver(fitDisk);
  observer.observe(diskArea);

  // ----- отрисовка -----

  function setSector(x: number, y: number, type: SectorType): void {
    const cell = sectors[y]![x]!;
    cell.className = `sector ${type}`;
    if (x === payload.start.x && y === payload.start.y) cell.classList.add("start");
  }

  function renderField(field: SectorType[][]): void {
    field.forEach((row, y) => row.forEach((type, x) => setSector(x, y, type)));
  }

  function placeRobot(point: { x: number; y: number }, instant = false): void {
    if (instant) robot.style.transition = "none";
    robot.style.setProperty("--rx", String(point.x));
    robot.style.setProperty("--ry", String(point.y));
    if (instant) {
      void robot.offsetWidth;
      robot.style.transition = "";
    }
  }

  function renderStack(): void {
    slots.forEach((slot, index) => {
      const cmd = state.program[index];
      slot.className = "slot";
      slot.innerHTML = cmd ? cmdIcon(cmd) : "";
      if (cmd) slot.classList.add("filled");
      if (cmd === "fix") slot.classList.add("is-fix");
    });
  }

  function markCurrent(index: number): void {
    slots.forEach((slot, i) => {
      slot.classList.toggle("current", i === index);
      slot.classList.toggle("done", index >= 0 ? i < index : false);
    });
  }

  let typing: ReturnType<typeof setInterval> | null = null;
  function say(text: string, tone: "info" | "ok" | "error" = "info"): void {
    if (typing) clearInterval(typing);
    lcd.dataset.tone = tone;
    let i = 0;
    lcdText.textContent = "";
    typing = setInterval(() => {
      i++;
      lcdText.textContent = text.slice(0, i);
      if (i % 3 === 0) sfx.lcd();
      if (i >= text.length && typing) clearInterval(typing);
    }, 20);
  }

  function setPhase(phase: typeof state.phase): void {
    state.phase = phase;
    l2.dataset.phase = phase;
    consoleEl.classList.toggle("running", phase === "run");
  }

  const sectorCenter = (x: number, y: number): { x: number; y: number } => ({
    x: ((x + 0.5) / cols) * 100,
    y: ((y + 0.5) / rows) * 100,
  });

  // ----- ввод -----

  function addCommand(cmd: Command): void {
    if (state.program.length >= stackCapacity) {
      sfx.deny();
      say("Стек заполнен", "error");
      return;
    }
    state.program.push(cmd);
    sfx.key(["up", "right", "down", "left", "fix"].indexOf(cmd));
    vibrate(8);
    renderStack();
    slots[state.program.length - 1]?.classList.add("pop");
  }

  function undo(): void {
    if (!state.program.length) {
      sfx.deny();
      return;
    }
    state.program.pop();
    sfx.undo();
    vibrate(8);
    ctx.event("undo");
    renderStack();
  }

  consoleEl.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLElement>("button");
    if (!btn || state.phase !== "edit") return;
    if (btn.dataset.cmd) addCommand(btn.dataset.cmd as Command);
    else if (btn.dataset.action === "undo") undo();
    else if (btn.dataset.action === "run") void run();
  });

  // ----- прогон программы -----

  async function run(): Promise<void> {
    if (!state.program.length) {
      sfx.deny();
      say("Программа пуста. Набери команды на пульте", "error");
      return;
    }

    setPhase("run");
    sfx.start();
    vibrate(20);
    say("Выполняю программу...");
    ctx.event("run", { length: state.program.length });
    await sleep(600);

    let crashed = false;
    for (let i = 0; i < state.program.length; i++) {
      if (!alive) return;
      markCurrent(i);
      const result = await execute(state.program[i]!);
      if (!alive) return;
      if (result === "crash") {
        crashed = true;
        break;
      }
    }
    markCurrent(-1);
    await sleep(300);
    if (!alive) return;

    const left = state.field.flat().filter((type) => type === "weak").length;

    if (crashed) {
      const { x, y } = state.robot;
      robot.classList.add("crashed");
      sectors[y]?.[x]?.classList.add("hit");
      sfx.error();
      vibrate([80, 40, 80]);
      say("Ошибка чтения сектора диска!", "error");
      ctx.event("crash", { x, y });
    } else if (left > 0) {
      sfx.deny();
      say("Найдены ещё сбойные сектора на диске", "error");
    }

    // последнее слово за сервером: он прогоняет ту же программу у себя
    const result = await ctx.submit({ program: state.program });
    if (!alive) return;

    if (result.solved) {
      await success(result);
      return;
    }

    await sleep(1600);
    if (!alive) return;
    setPhase("done");
    ctx.finished(result);
  }

  async function execute(cmd: Command): Promise<"ok" | "crash"> {
    const { x, y } = state.robot;

    if (cmd === "fix") {
      robotImg.animate(
        [
          { transform: "rotate(0)" },
          { transform: "rotate(-14deg)" },
          { transform: "rotate(14deg)" },
          { transform: "rotate(0)" },
        ],
        { duration: 360, easing: "ease-in-out" },
      );
      await sleep(160);
      if (state.field[y]![x] === "weak") {
        state.field[y]![x] = "fixed";
        setSector(x, y, "fixed");
        sfx.repair();
        vibrate(20);
        const center = sectorCenter(x, y);
        for (let i = 0; i < 8; i++) spawnSpark(disk, center.x, center.y, 0.5);
      } else {
        sfx.miss();
      }
      await sleep(stepMs);
      return "ok";
    }

    const [dx, dy] = MOVES[cmd];
    const nx = x + dx;
    const ny = y + dy;
    const inside = nx >= 0 && ny >= 0 && nx < cols && ny < rows;

    if (!inside) {
      if (payload.edgeBehavior === "crash") {
        sfx.error();
        return "crash";
      }
      // Край диска: робот упирается и остаётся на месте
      sfx.bump();
      robotImg.animate(
        [
          { transform: "translate(0, 0)" },
          { transform: `translate(${dx * 18}%, ${dy * 18}%)` },
          { transform: "translate(0, 0)" },
        ],
        { duration: 260, easing: "ease-out" },
      );
      await sleep(stepMs);
      return "ok";
    }

    state.robot = { x: nx, y: ny };
    placeRobot(state.robot);
    sfx.step();
    await sleep(stepMs);
    return state.field[ny]![nx] === "bad" ? "crash" : "ok";
  }

  async function success(result: Awaited<ReturnType<EngineContext["submit"]>>): Promise<void> {
    setPhase("done");
    say("Отлично! Все сектора починили", "ok");
    sfx.success();
    vibrate([30, 60, 30]);
    await sleep(1600);
    if (!alive) return;

    // Отходим от шкафа, чтобы створки закрывались на виду
    setPullback(true, true);
    sfx.pullback();
    await sleep(1000);
    if (!alive) return;

    cabinet.classList.add("closing");
    cabinet.classList.remove("open");
    sfx.cabinetClose(1.55);
    await sleep(2000);
    if (!alive) return;

    sfx.whoosh();
    whiteout.classList.add("show");
    await sleep(1000);
    if (!alive) return;
    ctx.finished(result);
  }

  // ---------- Заставка ----------

  function setPullback(on: boolean, animated: boolean): void {
    if (!animated) {
      l2.style.transition = "none";
      cabStage.style.transition = "none";
    }
    screen.classList.toggle("pullback", on);
    if (!animated) {
      void l2.offsetWidth;
      l2.style.transition = "";
      cabStage.style.transition = "";
    }
  }

  function setCabinet(open: boolean, animated: boolean): void {
    cabinet.classList.remove("closing");
    if (!animated) cabinet.classList.add("no-anim");
    cabinet.classList.toggle("open", open);
    if (!animated) {
      void cabinet.offsetWidth;
      cabinet.classList.remove("no-anim");
    }
  }

  function setWhite(on: boolean, animated: boolean): void {
    if (!animated) whiteout.style.transition = "none";
    whiteout.classList.toggle("show", on);
    if (!animated) {
      void whiteout.offsetWidth;
      whiteout.style.transition = "";
    }
  }

  function glitchLcd(): void {
    const chars = "#%&@$*!?01<>/\\";
    let s = "";
    for (let i = 0; i < 44; i++) s += chars[Math.floor(Math.random() * chars.length)];
    lcd.dataset.tone = "error";
    lcdText.textContent = s;
  }

  async function playIntro(): Promise<void> {
    cut = new Cutscene();
    const c = cut;
    setPhase("intro");

    // Экран уровня за шкафом: диск пока целый, робота нет
    renderField(payload.field.map((row) => row.map(() => "ok" as SectorType)));
    renderStack();
    markCurrent(-1);
    placeRobot(state.robot, true);
    robot.classList.add("off");
    robot.classList.remove("crashed");
    if (typing) clearInterval(typing);
    lcdText.textContent = "";

    virusReset(virus, "backHidden");
    setPullback(true, false);
    setCabinet(false, false);
    city.hidden = false;
    setWhite(true, false);

    try {
      // Кибергород проступает из белого
      await c.later(50);
      setWhite(false, true);
      sfx.city();
      c.track(
        cityStage.animate([{ transform: "scale(1)" }, { transform: "scale(1.12)" }], {
          duration: 3400,
          easing: "ease-out",
          fill: "forwards",
        }),
      );
      c.track(
        caption.animate(
          [
            { opacity: 0, letterSpacing: "0.6em" },
            { opacity: 1, letterSpacing: "0.3em", offset: 0.3 },
            { opacity: 1, letterSpacing: "0.3em", offset: 0.75 },
            { opacity: 0, letterSpacing: "0.3em" },
          ],
          { duration: 2200, delay: 400, fill: "forwards" },
        ),
      );
      await c.later(2700);

      // Наезд на шкаф
      sfx.zoom();
      c.track(
        cityStage.animate([{ transform: "scale(1.12)" }, { transform: "scale(4.2)" }], {
          duration: 1100,
          easing: "cubic-bezier(.6,0,.9,.5)",
          fill: "forwards",
        }),
      );
      c.track(
        city.animate([{ opacity: 1 }, { opacity: 1, offset: 0.55 }, { opacity: 0 }], {
          duration: 1100,
          fill: "forwards",
        }),
      );
      const cabZoom = c.track(
        cabStage.animate(
          [
            { transform: "translate(-50%, -50%) scale(0.3)" },
            { transform: "translate(-50%, -50%) scale(0.62)" },
          ],
          { duration: 1100, easing: "cubic-bezier(.3,0,.2,1)", fill: "forwards" },
        ),
      );
      await c.later(1600);
      city.hidden = true;
      cabZoom.cancel(); // дальше масштабом шкафа управляет класс pullback

      // Створки распахиваются на виду, за ними уже возится вирус
      const tinker = virusTinker(c, virus);
      const sparks = c.every(110, () => {
        spawnSpark(consoleFx, rand(35, 65), rand(30, 60));
        if (Math.random() < 0.5) sfx.tinker();
      });
      const lcdGlitch = c.every(120, glitchLcd);
      setCabinet(true, true);
      sfx.cabinetOpen();
      await c.later(1300);

      // Камера входит внутрь шкафа
      setPullback(false, true);
      sfx.pullback();

      const damage: { x: number; y: number; type: SectorType }[] = [];
      payload.field.forEach((row, y) =>
        row.forEach((type, x) => {
          if (type !== "ok") damage.push({ x, y, type });
        }),
      );
      shuffle(damage);
      const breakOne = (): void => {
        const d = damage.pop();
        if (!d) return;
        setSector(d.x, d.y, d.type);
        sectors[d.y]![d.x]!.classList.add("corrupting");
        sfx.corrupt();
      };
      await c.later(700);
      c.every(Math.max(60, Math.floor(2000 / Math.max(damage.length, 1))), breakOne);
      await c.later(2300);
      while (damage.length) breakOne();
      clearInterval(sparks);

      await virusScare(c, virus, tinker);
      virusRun(c, virus);
      await c.later(500);
      clearInterval(lcdGlitch);
      lcdText.textContent = "";
      await c.later(300);

      // Робот включается в своём углу
      robot.classList.remove("off");
      sfx.boot();
      await c.later(500);
    } catch {
      return;
    }
    finishIntro();
  }

  function stopIntro(): void {
    if (!cut) return;
    cut.stop();
    cut = null;
    consoleFx.replaceChildren();
    virusHide(virus);
  }

  function showLevel(): void {
    city.hidden = true;
    setPullback(false, false);
    setCabinet(true, false);
    setWhite(false, false);
    robot.classList.remove("off", "crashed");
    renderField(state.field);
    renderStack();
    markCurrent(-1);
    placeRobot(state.robot, true);
    fitDisk();
    say("Сбойные сектора! Составь программу для робота и нажми «Пуск»");
    setPhase("edit");
  }

  function finishIntro(): void {
    stopIntro();
    showLevel();
    ctx.introDone();
  }

  if (ctx.playIntro) void playIntro();
  else finishIntro();

  return {
    destroy() {
      alive = false;
      stopIntro();
      observer.disconnect();
      if (typing) clearInterval(typing);
      root.innerHTML = "";
    },
    skipIntro: finishIntro,
    canHint: () => state.phase === "edit",
    applyHint(hint: Hint) {
      const data = hint.data as { commands: Command[] };
      say(`Подсказка: ${data.commands.map((cmd) => KEY_LABEL[cmd].toLowerCase()).join(" → ")}`, "ok");
    },
  };
}
